import React, { useState, useEffect } from'react';
import { Link, useNavigate } from'react-router-dom';
import { motion } from'framer-motion';
import { Search, MapPin, Star, Calendar, Users, ChevronRight, Heart, Music, Disc3, Shield, CheckCircle, Navigation, Loader2, Trophy, ExternalLink } from'lucide-react';
import { Button } from'../components/ui/button';
import { Input } from'../components/ui/input';
import { Badge } from'../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from'../components/ui/select';
import { useTheme } from'../context/ThemeContext';
import { useAuth } from'../context/AuthContext';
import { getEventImage } from'../utils/eventImages';

import { API_URL } from'../utils/api';

// Generate SEO-friendly event URL
const getEventUrl = (event) => {
 if (event.full_slug) {
 return `/events/${event.full_slug}`;
 } else if (event.host_slug && event.slug) {
 return `/events/${event.host_slug}/${event.slug}`;
 } else if (event.slug) {
 return `/events/${event.slug}`;
 }
 return `/events/${event.event_id}`;
};

const heroImages = [
 {
 url:"https://static.prod-images.emergentagent.com/jobs/f6783fbe-a9ce-415b-8816-9076f9ce2a87/images/69d5b3d6974a81b325f7be54715f05e8615ab4e6bce14d9ce2dd710949032bc7.png",
 alt:"Line dancers in synchronized formation with purple lighting"
 },
 {
 url:"https://static.prod-images.emergentagent.com/jobs/f6783fbe-a9ce-415b-8816-9076f9ce2a87/images/a25b60625d456336cc6faf4419089c7ec45b8466868fa494c9cd9736771850e9.png",
 alt:"Cowboy boots on dance floor with purple lighting"
 },
 {
 url:"https://static.prod-images.emergentagent.com/jobs/f6783fbe-a9ce-415b-8816-9076f9ce2a87/images/ff49b4127a0591960b40099bf65a8c4810d0a523c4be0dd2d50acb65d4a3b422.png",
 alt:"Happy group at country line dance social event"
 }
];

const fadeInUp = {
 initial: { opacity: 0, y: 30 },
 animate: { opacity: 1, y: 0 },
 transition: { duration: 0.6 }
};

const staggerContainer = {
 animate: {
 transition: {
 staggerChildren: 0.1
 }
 }
};

export default function HomePage() {
 const [searchQuery, setSearchQuery] = useState('');
 const [featuredClubs, setFeaturedClubs] = useState([]);
 const [featuredDJs, setFeaturedDJs] = useState([]);
 const [events, setEvents] = useState([]);
 const [allClubs, setAllClubs] = useState([]);
 const [allDJs, setAllDJs] = useState([]);
 const [publicStats, setPublicStats] = useState(null);
 const [topDances, setTopDances] = useState([]);
 const [linedancerDances, setLinedancerDances] = useState([]);
 const [eldDances, setEldDances] = useState([]);
 const [mounted, setMounted] = useState(false);
 const navigate = useNavigate();
 
 // Location/distance filter state
 const [locationInput, setLocationInput] = useState('');
 const [distance, setDistance] = useState('nationwide');
 const [userCoords, setUserCoords] = useState(null);
 const [gettingLocation, setGettingLocation] = useState(false);
 const { isDark } = useTheme();
 const { user } = useAuth();

 // Track if component is mounted for hydration-safe rendering
 useEffect(() => {
 setMounted(true);
 }, []);

 useEffect(() => {
 fetchFeaturedClubs();
 fetchFeaturedDJs();
 fetchEvents();
 fetchAllClubs();
 fetchAllDJs();
 fetchPublicStats();
 fetchTopDances();
 fetchLinedancerDances();
 fetchEldDances();
 }, []);

 const fetchPublicStats = async () => {
 try {
 const response = await fetch(`${API_URL}/api/public/stats`);
 if (response.ok) {
 const data = await response.json();
 setPublicStats(data);
 }
 } catch (error) {
 console.error('Error fetching public stats:', error);
 }
 };

 const fetchTopDances = async () => {
 try {
 const response = await fetch(`${API_URL}/api/top-dances?limit=5`);
 if (response.ok) {
 const data = await response.json();
 setTopDances(data.dances || []);
 }
 } catch (error) {
 console.error('Error fetching top dances:', error);
 }
 };

 const fetchLinedancerDances = async () => {
 try {
 const response = await fetch(`${API_URL}/api/top-dances/linedancer?limit=5`);
 if (response.ok) {
 const data = await response.json();
 setLinedancerDances(data.dances || []);
 }
 } catch (error) {
 console.error('Error fetching linedancer dances:', error);
 }
 };

 const fetchEldDances = async () => {
 try {
 const response = await fetch(`${API_URL}/api/top-dances/eld?limit=5`);
 if (response.ok) {
 const data = await response.json();
 setEldDances(data.dances || []);
 }
 } catch (error) {
 console.error('Error fetching ELD dances:', error);
 }
 };

 const fetchFeaturedClubs = async () => {
 try {
 const response = await fetch(`${API_URL}/api/clubs/featured`);
 if (response.ok) {
 const data = await response.json();
 setFeaturedClubs(data);
 }
 } catch (error) {
 console.error('Error fetching featured clubs:', error);
 }
 };

 const fetchFeaturedDJs = async () => {
 try {
 const response = await fetch(`${API_URL}/api/djs/featured`);
 if (response.ok) {
 const data = await response.json();
 setFeaturedDJs(data);
 }
 } catch (error) {
 console.error('Error fetching featured DJs:', error);
 }
 };

 const fetchEvents = async () => {
 try {
 // Fetch both club events and community events
 const [clubEventsRes, communityEventsRes] = await Promise.all([
 fetch(`${API_URL}/api/events?upcoming=true`),
 fetch(`${API_URL}/api/community-events`)
 ]);
 
 let allEvents = [];
 
 if (clubEventsRes.ok) {
 const clubEvents = await clubEventsRes.json();
 allEvents = [...allEvents, ...clubEvents.map(e => ({ ...e, type:'club' }))];
 }
 
 if (communityEventsRes.ok) {
 const communityEvents = await communityEventsRes.json();
 allEvents = [...allEvents, ...communityEvents.map(e => ({ ...e, type:'community' }))];
 }
 
 // Sort by date and take first 4
 allEvents.sort((a, b) => new Date(a.event_date) - new Date(b.event_date));
 setEvents(allEvents.slice(0, 4));
 } catch (error) {
 console.error('Error fetching events:', error);
 }
 };

 const fetchAllClubs = async () => {
 try {
 const response = await fetch(`${API_URL}/api/clubs`);
 if (response.ok) {
 const data = await response.json();
 setAllClubs(data.slice(0, 6));
 }
 } catch (error) {
 console.error('Error fetching clubs:', error);
 }
 };

 const fetchAllDJs = async () => {
 try {
 const response = await fetch(`${API_URL}/api/djs`);
 if (response.ok) {
 const data = await response.json();
 setAllDJs(data.slice(0, 4));
 }
 } catch (error) {
 console.error('Error fetching DJs:', error);
 }
 };

 // Get user's location via browser geolocation
 const getUserLocation = () => {
 if (!navigator.geolocation) {
 alert('Geolocation is not supported by your browser');
 return;
 }
 
 setGettingLocation(true);
 navigator.geolocation.getCurrentPosition(
 (position) => {
 setUserCoords({
 lat: position.coords.latitude,
 lng: position.coords.longitude
 });
 setLocationInput('Using your location');
 setGettingLocation(false);
 },
 (error) => {
 console.error('Geolocation error:', error);
 alert('Unable to get your location. Please enter a postcode or town.');
 setGettingLocation(false);
 },
 { enableHighAccuracy: true, timeout: 10000 }
 );
 };

 // Geocode a postcode/location
 const geocodeLocation = async (location) => {
 try {
 const response = await fetch(`${API_URL}/api/geocode?address=${encodeURIComponent(location)}`);
 if (response.ok) {
 const data = await response.json();
 return { lat: data.latitude, lng: data.longitude };
 }
 } catch (error) {
 console.error('Geocoding error:', error);
 }
 return null;
 };

 const handleSearch = async (e) => {
 e.preventDefault();
 const params = new URLSearchParams();
 
 if (searchQuery.trim()) {
 params.set('search', searchQuery);
 }
 
 // Handle distance filtering
 if (distance !=='nationwide') {
 let coords = userCoords;
 
 // If user entered a location manually, geocode it
 if (locationInput && locationInput !=='Using your location' && !userCoords) {
 coords = await geocodeLocation(locationInput);
 }
 
 if (coords) {
 params.set('lat', coords.lat);
 params.set('lng', coords.lng);
 params.set('distance', distance);
 }
 }
 
 navigate(`/clubs${params.toString() ?'?' + params.toString() :''}`);
 };

 const handleLocationInputChange = (e) => {
 setLocationInput(e.target.value);
 // Clear user coords if they're typing a new location
 if (userCoords && e.target.value !=='Using your location') {
 setUserCoords(null);
 }
 };

 const renderStars = (rating) => {
 return [...Array(5)].map((_, i) => (
 <Star
 key={i}
 size={16}
 className={i < Math.floor(rating) ?'fill-violet-500 text-violet-500' :'text-slate-300'}
 />
 ));
 };

 // Show featured clubs or fallback to all clubs
 const displayClubs = featuredClubs.length > 0 ? featuredClubs : allClubs;
 const displayDJs = featuredDJs.length > 0 ? featuredDJs : allDJs;

 return (
 <div className="min-h-screen bg-slate-50 transition-colors duration-200" data-testid="homepage">
 {/* Hero Section */}
 <section 
 className="relative transition-all duration-200"
 style={{
 background: mounted && isDark 
 ?'linear-gradient(135deg, #020617 0%, #0f172a 50%, rgba(30, 27, 75, 0.5) 100%)'
 :'linear-gradient(135deg, rgba(245, 243, 255, 0.5) 0%, white 50%, rgba(250, 245, 255, 1) 100%)'
 }}
 >
 <div className="absolute inset-0 noise-overlay" />
 {/* Subtle western pattern */}
 <div className="absolute top-0 right-0 w-96 h-96 opacity-[0.03]" style={{
 backgroundImage: `url("data:image/svg+xml,%3Csvg width='80' height='80' viewBox='0 0 80 80' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M40 0L80 40L40 80L0 40z' fill='%238B4513' /%3E%3C/svg%3E")`,
 backgroundSize:'40px 40px'
 }} />
 
 <div className="container mx-auto px-6 lg:px-12 pt-2 pb-6 lg:pt-4 lg:pb-8">
 <div className="grid lg:grid-cols-5 gap-8 items-start">
 {/* Left Content - 3 cols */}
 <motion.div 
 className="lg:col-span-3 space-y-4"
 initial="initial"
 animate="animate"
 variants={staggerContainer}
 >
 {/* Large Hero Logo */}
 <motion.div variants={fadeInUp} className="flex justify-center lg:justify-start">
 <img 
 src="https://customer-assets.emergentagent.com/job_dancer-community/artifacts/buaegm7r_1D08A5CF-84D3-4ADA-9A3D-0F8EBE77862A.png" 
 alt="Line Dance Central Logo"
 className="w-64 h-64 sm:w-80 sm:h-80 lg:w-[420px] lg:h-[420px] object-contain"
 />
 </motion.div>
 
 <motion.h1 
 variants={fadeInUp}
 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-slate-900 leading-tight"
 style={{ fontFamily:'Syne, sans-serif' }}
 >
 Discover Local{''}
 <span className="text-violet-500">Line Dance</span>{''}
 Clubs Near You
 </motion.h1>
 
 <motion.p 
 variants={fadeInUp}
 className="text-lg text-slate-500 max-w-xl"
 >
 Round up your boots and join vibrant dance communities. Find expert instructors and two-step into your city's best honky-tonks and dance halls.
 </motion.p>
 
 {/* Search Bar */}
 <motion.form 
 variants={fadeInUp}
 onSubmit={handleSearch}
 className="space-y-4"
 >
 <div className="flex flex-col sm:flex-row gap-3">
 <div className="relative flex-1">
 <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-300 w-5 h-5" />
 <Input
 type="text"
 placeholder="Search by club name or style..."
 value={searchQuery}
 onChange={(e) => setSearchQuery(e.target.value)}
 className="pl-12 h-12 rounded-xl border-slate-200 bg-white focus:ring-2 focus:ring-violet-500 focus:border-transparent text-base"
 data-testid="hero-search-input"
 />
 </div>
 </div>
 
 {/* Location & Distance Filters */}
 <div className="flex flex-col sm:flex-row gap-3">
 <div className="relative flex-1">
 <MapPin className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-300 w-5 h-5" />
 <Input
 type="text"
 placeholder="Enter postcode or town..."
 value={locationInput}
 onChange={handleLocationInputChange}
 className="pl-12 pr-12 h-12 rounded-xl border-slate-200 bg-white focus:ring-2 focus:ring-violet-500 focus:border-transparent text-base"
 data-testid="location-input"
 />
 <Button
 type="button"
 variant="ghost"
 size="icon"
 onClick={getUserLocation}
 disabled={gettingLocation}
 aria-label="Use my location"
 title="Use my location"
 className="absolute right-3 top-1/2 -translate-y-1/2 h-8 w-8 text-violet-600 hover:text-violet-600 hover:bg-violet-50"
 >
 {gettingLocation ? (
 <Loader2 className="w-5 h-5 animate-spin" />
 ) : (
 <Navigation className="w-5 h-5" />
 )}
 </Button>
 </div>
 
 <Select value={distance} onValueChange={setDistance}>
 <SelectTrigger className="w-full sm:w-40 h-12 rounded-xl border-slate-200 bg-white" data-testid="distance-select">
 <SelectValue placeholder="Distance" />
 </SelectTrigger>
 <SelectContent>
 <SelectItem value="5">Within 5 miles</SelectItem>
 <SelectItem value="10">Within 10 miles</SelectItem>
 <SelectItem value="25">Within 25 miles</SelectItem>
 <SelectItem value="50">Within 50 miles</SelectItem>
 <SelectItem value="nationwide">Nationwide</SelectItem>
 </SelectContent>
 </Select>
 
 <Button 
 type="submit"
 className="h-12 px-8 bg-violet-600 hover:bg-violet-500 text-white rounded-xl font-bold shadow-lg shadow-violet-500/25 transition-all hover:scale-[1.02]"
 data-testid="hero-search-btn"
 >
 <Search className="w-5 h-5 mr-2" />
 Search
 </Button>
 </div>
 </motion.form>
 
 </motion.div>
 
 {/* Right Images - 2 cols */}
 <motion.div 
 className="lg:col-span-2 hidden lg:block self-start"
 initial={{ opacity: 0, x: 50 }}
 animate={{ opacity: 1, x: 0 }}
 transition={{ duration: 0.8, delay: 0.3 }}
 >
 <div className="grid grid-cols-2 gap-4 h-[560px]">
 <div className="row-span-2 rounded-2xl overflow-hidden shadow-2xl">
 <img 
 src={heroImages[0].url} 
 alt={heroImages[0].alt}
 className="w-full h-full object-cover"
 />
 </div>
 <div className="rounded-2xl overflow-hidden shadow-xl">
 <img 
 src={heroImages[1].url} 
 alt={heroImages[1].alt}
 className="w-full h-full object-cover"
 />
 </div>
 <div className="rounded-2xl overflow-hidden shadow-xl">
 <img 
 src={heroImages[2].url} 
 alt={heroImages[2].alt}
 className="w-full h-full object-cover"
 />
 </div>
 </div>
 </motion.div>
 </div>
 </div>
 </section>

 {/* Quick Stats Section - Only show if enabled and stats exist */}
 {publicStats?.show_stats && (
 <section className="py-12 bg-slate-100 border-y border-slate-200 transition-colors duration-200" data-testid="homepage-stats-section">
 <div className="container mx-auto px-6 lg:px-12">
 <motion.div 
 className="flex flex-wrap justify-center gap-8 lg:gap-16"
 initial={{ opacity: 0, y: 20 }}
 whileInView={{ opacity: 1, y: 0 }}
 viewport={{ once: true }}
 data-testid="homepage-stats"
 >
 <div className="flex items-center gap-4">
 <div className="w-14 h-14 rounded-2xl bg-violet-100 flex items-center justify-center">
 <Users className="w-7 h-7 text-violet-600" />
 </div>
 <div>
 <p className="font-bold text-slate-900 text-2xl">{publicStats.users || 0}</p>
 <p className="text-sm text-slate-300">Registered Users</p>
 </div>
 </div>
 <div className="flex items-center gap-4">
 <div className="w-14 h-14 rounded-2xl bg-violet-100 flex items-center justify-center">
 <MapPin className="w-7 h-7 text-violet-600" />
 </div>
 <div>
 <p className="font-bold text-slate-900 text-2xl">{publicStats.clubs || 0}</p>
 <p className="text-sm text-slate-300">Dance Clubs</p>
 </div>
 </div>
 <div className="flex items-center gap-4">
 <div className="w-14 h-14 rounded-2xl bg-violet-100 flex items-center justify-center">
 <Calendar className="w-7 h-7 text-violet-600" />
 </div>
 <div>
 <p className="font-bold text-slate-900 text-2xl">{publicStats.events || 0}</p>
 <p className="text-sm text-slate-300">Upcoming Events</p>
 </div>
 </div>
 </motion.div>
 </div>
 </section>
 )}

 {/* Featured Clubs Section */}
 <section className="py-20 bg-white transition-colors duration-200" data-testid="featured-clubs-section">
 <div className="container mx-auto px-6 lg:px-12">
 <motion.div 
 className="flex flex-col md:flex-row md:items-end justify-between mb-12"
 initial={{ opacity: 0, y: 20 }}
 whileInView={{ opacity: 1, y: 0 }}
 viewport={{ once: true }}
 >
 <div>
 <h2 className="text-3xl lg:text-4xl font-extrabold text-slate-900 mb-3" style={{ fontFamily:'Syne, sans-serif' }}>
 {featuredClubs.length > 0 ?'Featured Clubs' :'Popular Clubs'}
 </h2>
 <p className="text-slate-500 max-w-lg">
 {featuredClubs.length > 0 
 ?'Top-rated venues handpicked for exceptional dance experiences'
 :'Discover our most popular line dance venues'}
 </p>
 </div>
 <Link 
 to="/clubs" 
 className="mt-4 md:mt-0 inline-flex items-center text-violet-600 font-semibold hover:text-violet-700 transition-colors"
 data-testid="view-all-clubs-link"
 >
 View All Clubs
 <ChevronRight className="w-5 h-5 ml-1" />
 </Link>
 </motion.div>

 {displayClubs.length === 0 ? (
 <motion.div 
 className="text-center py-16 bg-slate-50 rounded-2xl"
 initial={{ opacity: 0 }}
 whileInView={{ opacity: 1 }}
 viewport={{ once: true }}
 >
 <Music className="w-16 h-16 text-slate-300 mx-auto mb-4" />
 <h3 className="text-xl font-semibold text-slate-700 mb-2">No clubs yet</h3>
 <p className="text-slate-300 mb-6">Be the first to add a line dance club!</p>
 <Link to="/dashboard/add-club">
 <Button className="bg-violet-600 hover:bg-violet-500 text-white" data-testid="add-first-club-btn">
 Add Your Club
 </Button>
 </Link>
 </motion.div>
 ) : (
 <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
 {displayClubs.map((club, index) => (
 <motion.div
 key={club.club_id}
 initial={{ opacity: 0, y: 30 }}
 whileInView={{ opacity: 1, y: 0 }}
 viewport={{ once: true }}
 transition={{ delay: index * 0.1 }}
 >
 <Link to={`/clubs/${club.slug || club.club_id}`} data-testid={`club-card-${club.club_id}`}>
 <div className="group relative overflow-hidden rounded-2xl bg-white border border-slate-100 shadow-sm hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
 <div className="relative h-48 overflow-hidden">
 <img
 src={club.image_url ||'https://static.prod-images.emergentagent.com/jobs/f6783fbe-a9ce-415b-8816-9076f9ce2a87/images/ff49b4127a0591960b40099bf65a8c4810d0a523c4be0dd2d50acb65d4a3b422.png'}
 alt={club.name}
 className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
 />
 {club.is_featured && (
 <div className="absolute top-3 left-3">
 <Badge className="bg-violet-500 text-white hover:bg-violet-500">Featured</Badge>
 </div>
 )}
 <button 
 className="absolute top-3 right-3 w-9 h-9 bg-white/90 backdrop-blur-sm rounded-full flex items-center justify-center hover:bg-white transition-colors"
 onClick={(e) => e.preventDefault()}
 >
 <Heart className="w-5 h-5 text-slate-500 hover:text-violet-500" />
 </button>
 </div>
 <div className="p-5">
 <div className="flex items-start justify-between mb-2">
 <h3 className="text-lg font-bold text-slate-900 group-hover:text-violet-600 transition-colors">
 {club.name}
 </h3>
 <div className="flex items-center gap-1">
 {renderStars(club.rating)}
 <span className="text-sm text-slate-300 ml-1">({club.review_count})</span>
 </div>
 </div>
 <p className="flex items-center text-slate-300 text-sm mb-1">
 <MapPin className="w-4 h-4 mr-1" />
 {club.location || [club.city, club.region, club.country].filter(Boolean).join(',') ||'Location not specified'}
 </p>
 {club.additional_locations && club.additional_locations.length > 0 && (
 <p className="text-xs text-slate-300 mb-3 pl-5">
 + {club.additional_locations.map(loc => loc.address).join(',')}
 </p>
 )}
 {(!club.additional_locations || club.additional_locations.length === 0) && <div className="mb-3" />}
 <p className="text-slate-500 text-sm line-clamp-2 mb-4">{club.description}</p>
 <div className="flex flex-wrap gap-2">
 {club.skill_levels?.slice(0, 3).map((level, i) => (
 <Badge 
 key={i} 
 variant="secondary"
 className={`text-xs ${
 level ==='Beginner' ?'bg-teal-100 text-teal-700' :
 level ==='Intermediate' ?'bg-violet-100 text-violet-700' :
'bg-slate-800 text-white'
 }`}
 >
 {level}
 </Badge>
 ))}
 </div>
 </div>
 </div>
 </Link>
 </motion.div>
 ))}
 </div>
 )}
 </div>
 </section>

 {/* Upcoming Events Section */}
 <section className="py-20 bg-slate-900" data-testid="events-section">
 <div className="container mx-auto px-6 lg:px-12">
 <motion.div 
 className="flex flex-col md:flex-row md:items-end justify-between mb-12"
 initial={{ opacity: 0, y: 20 }}
 whileInView={{ opacity: 1, y: 0 }}
 viewport={{ once: true }}
 >
 <div>
 <h2 className="text-3xl lg:text-4xl font-extrabold text-white mb-3" style={{ fontFamily:'Syne, sans-serif' }}>
 Upcoming Events
 </h2>
 <p className="text-slate-300 max-w-lg">
 Don't miss out on the best line dance events in your area
 </p>
 </div>
 <div className="mt-4 md:mt-0 flex items-center gap-4">
 <Link 
 to={user ?"/dashboard" :"/register"}
 className="inline-flex items-center px-4 py-2 bg-teal-500 hover:bg-teal-400 text-white font-semibold rounded-lg transition-colors"
 data-testid="add-event-btn"
 >
 <Calendar className="w-4 h-4 mr-2" />
 Add Your Event
 </Link>
 <Link 
 to="/events" 
 className="inline-flex items-center text-violet-500 font-semibold hover:text-violet-400 transition-colors"
 data-testid="view-all-events-link"
 >
 View All Events
 <ChevronRight className="w-5 h-5 ml-1" />
 </Link>
 </div>
 </motion.div>

 {events.length === 0 ? (
 <motion.div 
 className="text-center py-16 bg-slate-800 rounded-2xl"
 initial={{ opacity: 0 }}
 whileInView={{ opacity: 1 }}
 viewport={{ once: true }}
 >
 <Calendar className="w-16 h-16 text-slate-500 mx-auto mb-4" />
 <h3 className="text-xl font-semibold text-slate-300 mb-2">No upcoming events</h3>
 <p className="text-slate-300">Check back soon for new dance events!</p>
 </motion.div>
 ) : (
 <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
 {events.map((event, index) => (
 <motion.div
 key={event.event_id}
 initial={{ opacity: 0, y: 30 }}
 whileInView={{ opacity: 1, y: 0 }}
 viewport={{ once: true }}
 transition={{ delay: index * 0.1 }}
 >
 <Link to={getEventUrl(event)} data-testid={`event-card-${event.event_id}`}>
 <div className="bg-slate-800 rounded-xl overflow-hidden border border-slate-700 hover:border-violet-500/30 transition-all duration-300 hover:-translate-y-1 h-full group">
 <div className="relative h-36 overflow-hidden">
 <img
 src={getEventImage(event.event_id, event.image_url)}
 alt={event.title}
 className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
 />
 <div className="absolute top-3 left-3 bg-violet-500 text-white text-xs font-bold px-3 py-1.5 rounded-lg">
 {new Date(event.event_date).toLocaleDateString('en-GB', { month:'short', day:'numeric' })}
 </div>
 {event.type ==='community' && (
 <div className="absolute top-3 right-3 bg-blue-500 text-white text-xs font-bold px-2 py-1 rounded">
 Community
 </div>
 )}
 </div>
 <div className="p-4">
 <h3 className="text-white font-bold mb-1 line-clamp-1 group-hover:text-violet-400 transition-colors">{event.title}</h3>
 <p className="text-slate-300 text-sm mb-2 line-clamp-1">
 {event.type ==='community' ? (event.location ||'Local Event') : event.club_name}
 </p>
 <div className="flex items-center justify-between">
 <span className="text-teal-400 text-sm">{event.event_time}</span>
 {event.price && (
 <span className="text-violet-400 font-semibold">£{event.price}</span>
 )}
 </div>
 </div>
 </div>
 </Link>
 </motion.div>
 ))}
 </div>
 )}
 </div>
 </section>

 {/* Featured DJs Section - Controlled by admin toggle */}
 {publicStats?.show_djs !== false && (
 <section className="py-20 bg-gradient-to-br from-violet-50 to-purple-50 transition-colors duration-200" data-testid="djs-section">
 <div className="container mx-auto px-6 lg:px-12">
 <motion.div 
 className="flex flex-col md:flex-row md:items-end justify-between mb-12"
 initial={{ opacity: 0, y: 20 }}
 whileInView={{ opacity: 1, y: 0 }}
 viewport={{ once: true }}
 >
 <div>
 <h2 className="text-3xl lg:text-4xl font-extrabold text-slate-900 mb-3" style={{ fontFamily:'Syne, sans-serif' }}>
 {featuredDJs.length > 0 ?'Featured DJs' :'Country DJs'}
 </h2>
 <p className="text-slate-500 max-w-lg">
 Find the perfect DJ to keep the dance floor packed all night
 </p>
 </div>
 <Link 
 to="/djs" 
 className="mt-4 md:mt-0 inline-flex items-center text-violet-700 font-semibold hover:text-violet-800 transition-colors"
 data-testid="view-all-djs-link"
 >
 View All DJs
 <ChevronRight className="w-5 h-5 ml-1" />
 </Link>
 </motion.div>

 {displayDJs.length === 0 ? (
 <motion.div 
 className="text-center py-16 bg-white rounded-2xl"
 initial={{ opacity: 0 }}
 whileInView={{ opacity: 1 }}
 viewport={{ once: true }}
 >
 <Disc3 className="w-16 h-16 text-slate-300 mx-auto mb-4" />
 <h3 className="text-xl font-semibold text-slate-700 mb-2">No DJs yet</h3>
 <p className="text-slate-300 mb-6">Be the first to list yourself as a DJ!</p>
 <Link to="/dashboard/add-dj">
 <Button className="bg-violet-600 hover:bg-violet-500 text-white" data-testid="add-first-dj-btn">
 List Yourself as DJ
 </Button>
 </Link>
 </motion.div>
 ) : (
 <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
 {displayDJs.map((dj, index) => (
 <motion.div
 key={dj.dj_id}
 initial={{ opacity: 0, y: 30 }}
 whileInView={{ opacity: 1, y: 0 }}
 viewport={{ once: true }}
 transition={{ delay: index * 0.1 }}
 >
 <Link to={`/djs/${dj.dj_id}`} data-testid={`dj-card-${dj.dj_id}`}>
 <div className="group relative overflow-hidden rounded-2xl bg-white border border-slate-100 shadow-sm hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
 <div className="relative h-40 overflow-hidden bg-gradient-to-br from-violet-600 to-purple-700">
 {dj.image_url ? (
 <img
 src={dj.image_url}
 alt={dj.stage_name || dj.name}
 className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
 />
 ) : (
 <img
 src="https://static.prod-images.emergentagent.com/jobs/f6783fbe-a9ce-415b-8816-9076f9ce2a87/images/4fad027c0b6ceb7cac8dae85ef2ee610c7e3d96a2cc6f2d032a22e2350b0313e.png"
 alt="Country DJ"
 className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105 opacity-60"
 />
 )}
 {dj.is_featured && (
 <div className="absolute top-3 left-3">
 <Badge className="bg-violet-500 text-white hover:bg-violet-500">Featured</Badge>
 </div>
 )}
 </div>
 <div className="p-4">
 <h3 className="font-bold text-slate-900 group-hover:text-violet-600 transition-colors">
 {dj.stage_name || dj.name}
 </h3>
 <p className="flex items-center text-slate-300 text-sm mt-1">
 <MapPin className="w-4 h-4 mr-1" />
 {dj.location || [dj.location_city, dj.location_state].filter(Boolean).join(',') ||'Location not specified'}
 </p>
 <div className="flex flex-wrap gap-1 mt-3">
 {dj.genres?.slice(0, 2).map((genre, i) => (
 <Badge key={i} variant="secondary" className="bg-violet-100 text-violet-800 text-xs">
 {genre}
 </Badge>
 ))}
 </div>
 </div>
 </div>
 </Link>
 </motion.div>
 ))}
 </div>
 )}
 </div>
 </section>
 )}

 {/* Top Dances This Week Section */}
 {(topDances.length > 0 || linedancerDances.length > 0 || eldDances.length > 0) && (
 <section className="py-20 bg-white transition-colors duration-200" data-testid="top-dances-section">
 <div className="container mx-auto px-6 lg:px-12">
 <motion.div 
 className="flex flex-col md:flex-row md:items-end justify-between mb-12"
 initial={{ opacity: 0, y: 20 }}
 whileInView={{ opacity: 1, y: 0 }}
 viewport={{ once: true }}
 >
 <div>
 <div className="flex items-center gap-2 mb-3">
 <Trophy className="w-8 h-8 text-yellow-500" />
 <h2 className="text-3xl lg:text-4xl font-extrabold text-slate-900" style={{ fontFamily:'Syne, sans-serif' }}>
 Top Dances This Week
 </h2>
 </div>
 <p className="text-slate-500 max-w-lg">
 The most popular line dances from three leading sources
 </p>
 </div>
 <Link 
 to="/dances/popular" 
 className="mt-4 md:mt-0 inline-flex items-center text-violet-700 font-semibold hover:text-violet-800 transition-colors"
 data-testid="view-all-dances-link"
 >
 View Full Charts
 <ChevronRight className="w-5 h-5 ml-1" />
 </Link>
 </motion.div>

 {/* Three-column grid for all sources */}
 <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
 {/* CopperKnob Column */}
 {topDances.length > 0 && (
 <div>
 <div className="flex items-center gap-2 mb-4">
 <div className="w-8 h-8 rounded-lg bg-amber-100 flex items-center justify-center">
 <Trophy className="w-4 h-4 text-amber-600" />
 </div>
 <h3 className="font-bold text-slate-900">CopperKnob</h3>
 </div>
 <div className="space-y-2">
 {topDances.map((dance, index) => (
 <motion.a
 key={`ck-${dance.rank}`}
 href={dance.url ||'#'}
 target="_blank"
 rel="noopener noreferrer"
 onClick={(e) => {
 if (dance.url && dance.url.startsWith('http')) {
 e.preventDefault();
 e.stopPropagation();
 window.open(dance.url,'_blank','noopener,noreferrer');
 }
 }}
 initial={{ opacity: 0, x: -20 }}
 whileInView={{ opacity: 1, x: 0 }}
 viewport={{ once: true }}
 transition={{ delay: index * 0.05 }}
 className="group flex items-center gap-3 p-3 bg-slate-50 hover:bg-amber-50 border border-slate-100 rounded-lg transition-all cursor-pointer"
 data-testid={`home-dance-ck-${dance.rank}`}
 >
 <div className={`w-8 h-8 rounded-md flex items-center justify-center font-bold text-xs shrink-0 ${
 dance.rank === 1 ?'bg-yellow-400 text-yellow-900' :
 dance.rank === 2 ?'bg-slate-300 text-slate-800' :
 dance.rank === 3 ?'bg-amber-600 text-amber-100' :
'bg-slate-600 text-white'
 }`}>
 #{dance.rank}
 </div>
 
 <div className="flex-1 min-w-0">
 <h4 className="font-semibold text-sm text-slate-900 group-hover:text-amber-600 transition-colors truncate">
 {dance.name}
 </h4>
 <p className="text-xs text-slate-300 truncate">
 {dance.choreographer}
 </p>
 </div>
 
 <div className="hidden sm:block lg:hidden xl:block">
 <span className={`px-2 py-0.5 text-xs font-medium rounded ${
 dance.level.toLowerCase().includes('beginner') ?'bg-green-100 text-green-700' :
 dance.level.toLowerCase().includes('improver') ?'bg-blue-100 text-blue-700' :
 dance.level.toLowerCase().includes('intermediate') ?'bg-orange-100 text-orange-700' :
'bg-red-100 text-red-700'
 }`}>
 {dance.level}
 </span>
 </div>
 
 {dance.url && <ExternalLink className="w-3 h-3 text-slate-300 group-hover:text-amber-500 shrink-0" />}
 </motion.a>
 ))}
 </div>
 </div>
 )}

 {/* LineDancerWeb Column */}
 {linedancerDances.length > 0 && (
 <div>
 <div className="flex items-center gap-2 mb-4">
 <div className="w-8 h-8 rounded-lg bg-violet-100 flex items-center justify-center">
 <Trophy className="w-4 h-4 text-violet-600" />
 </div>
 <h3 className="font-bold text-slate-900">LineDancer UK</h3>
 <span className="text-xs text-slate-300 ml-auto">by votes</span>
 </div>
 <div className="space-y-2">
 {linedancerDances.map((dance, index) => (
 <motion.a
 key={`ld-${dance.rank}`}
 href={dance.url ||'#'}
 target="_blank"
 rel="noopener noreferrer"
 onClick={(e) => {
 if (dance.url && dance.url.startsWith('http')) {
 e.preventDefault();
 e.stopPropagation();
 window.open(dance.url,'_blank','noopener,noreferrer');
 }
 }}
 initial={{ opacity: 0, y: 20 }}
 whileInView={{ opacity: 1, y: 0 }}
 viewport={{ once: true }}
 transition={{ delay: index * 0.05 }}
 className="group flex items-center gap-3 p-3 bg-slate-50 hover:bg-violet-50 border border-slate-100 rounded-lg transition-all cursor-pointer"
 data-testid={`home-dance-ld-${dance.rank}`}
 >
 <div className={`w-8 h-8 rounded-md flex items-center justify-center font-bold text-xs shrink-0 ${
 dance.rank === 1 ?'bg-yellow-400 text-yellow-900' :
 dance.rank === 2 ?'bg-slate-300 text-slate-800' :
 dance.rank === 3 ?'bg-amber-600 text-amber-100' :
'bg-slate-600 text-white'
 }`}>
 #{dance.rank}
 </div>
 
 <div className="flex-1 min-w-0">
 <h4 className="font-semibold text-sm text-slate-900 group-hover:text-violet-600 transition-colors truncate">
 {dance.name}
 </h4>
 <p className="text-xs text-slate-300 truncate">
 {dance.choreographer}
 </p>
 </div>
 
 <div className="hidden sm:block lg:hidden xl:block">
 <span className={`px-2 py-0.5 text-xs font-medium rounded ${
 dance.level.toLowerCase().includes('beginner') ?'bg-green-100 text-green-700' :
 dance.level.toLowerCase().includes('improver') ?'bg-blue-100 text-blue-700' :
 dance.level.toLowerCase().includes('intermediate') ?'bg-orange-100 text-orange-700' :
'bg-red-100 text-red-700'
 }`}>
 {dance.level}
 </span>
 </div>
 
 {dance.url && <ExternalLink className="w-3 h-3 text-slate-300 group-hover:text-violet-500 shrink-0" />}
 </motion.a>
 ))}
 </div>
 </div>
 )}

 {/* ELD Most Viewed Column */}
 {eldDances.length > 0 && (
 <div>
 <div className="flex items-center gap-2 mb-4">
 <div className="w-8 h-8 rounded-lg bg-emerald-100 flex items-center justify-center">
 <Trophy className="w-4 h-4 text-emerald-600" />
 </div>
 <h3 className="font-bold text-slate-900">ELD Most Viewed</h3>
 </div>
 <div className="space-y-2">
 {eldDances.map((dance, index) => (
 <motion.a
 key={`eld-${dance.rank}`}
 href={dance.url ||'#'}
 target="_blank"
 rel="noopener noreferrer"
 onClick={(e) => {
 if (dance.url && dance.url.startsWith('http')) {
 e.preventDefault();
 e.stopPropagation();
 window.open(dance.url,'_blank','noopener,noreferrer');
 }
 }}
 initial={{ opacity: 0, x: 20 }}
 whileInView={{ opacity: 1, x: 0 }}
 viewport={{ once: true }}
 transition={{ delay: index * 0.05 }}
 className="group flex items-center gap-3 p-3 bg-slate-50 hover:bg-emerald-50 border border-slate-100 rounded-lg transition-all cursor-pointer"
 data-testid={`home-dance-eld-${dance.rank}`}
 >
 <div className={`w-8 h-8 rounded-md flex items-center justify-center font-bold text-xs shrink-0 ${
 dance.rank === 1 ?'bg-yellow-400 text-yellow-900' :
 dance.rank === 2 ?'bg-slate-300 text-slate-800' :
 dance.rank === 3 ?'bg-amber-600 text-amber-100' :
'bg-slate-600 text-white'
 }`}>
 #{dance.rank}
 </div>
 
 <div className="flex-1 min-w-0">
 <h4 className="font-semibold text-sm text-slate-900 group-hover:text-emerald-600 transition-colors truncate">
 {dance.name}
 </h4>
 <p className="text-xs text-slate-300 truncate">
 {dance.choreographer}
 </p>
 </div>
 
 <div className="hidden sm:block lg:hidden xl:block">
 <span className={`px-2 py-0.5 text-xs font-medium rounded ${
 dance.level.toLowerCase().includes('beginner') ?'bg-green-100 text-green-700' :
 dance.level.toLowerCase().includes('improver') ?'bg-blue-100 text-blue-700' :
 dance.level.toLowerCase().includes('intermediate') ?'bg-orange-100 text-orange-700' :
'bg-red-100 text-red-700'
 }`}>
 {dance.level}
 </span>
 </div>
 
 {dance.url && <ExternalLink className="w-3 h-3 text-slate-300 group-hover:text-emerald-500 shrink-0" />}
 </motion.a>
 ))}
 </div>
 </div>
 )}
 </div>
 </div>
 </section>
 )}

 {/* CTA Section */}
 <section className="py-20 bg-gradient-to-r from-violet-700 to-purple-600 relative overflow-hidden">
 <div className="absolute inset-0 noise-overlay" />
 {/* Subtle western pattern overlay */}
 <div className="absolute inset-0 opacity-5" style={{
 backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M30 0L60 30L30 60L0 30z' fill='%23ffffff' fill-opacity='0.4'/%3E%3C/svg%3E")`,
 backgroundSize:'30px 30px'
 }} />
 <div className="container mx-auto px-6 lg:px-12 relative z-10">
 <motion.div 
 className="max-w-3xl mx-auto text-center"
 initial={{ opacity: 0, y: 30 }}
 whileInView={{ opacity: 1, y: 0 }}
 viewport={{ once: true }}
 >
 <h2 className="text-3xl lg:text-4xl font-extrabold text-white mb-6" style={{ fontFamily:'Syne, sans-serif' }}>
 Run A Club?
 </h2>
 <p className="text-violet-100 text-lg mb-8">
 List your club for free and connect with dancers looking to join the Line Dance Community.
 </p>
 <div className="flex flex-col sm:flex-row gap-4 justify-center">
 <Link to="/dashboard/add-club">
 <Button 
 className="h-14 px-8 bg-white text-violet-600 hover:bg-slate-100 rounded-xl font-bold shadow-lg transition-all hover:scale-[1.02]"
 data-testid="cta-add-club-btn"
 >
 Add Your Club
 </Button>
 </Link>
 <Link to="/about">
 <Button 
 variant="outline"
 className="h-14 px-8 border-2 border-white text-white hover:bg-white/10 rounded-xl font-bold transition-all"
 data-testid="cta-learn-more-btn"
 >
 Learn More
 </Button>
 </Link>
 </div>
 </motion.div>
 </div>
 </section>
 </div>
 );
}
